import React, { useEffect, useState } from "react";
import { pushToast } from "../../Toaster/Toaster.slice";
import { CSwitch, CFormGroup, CSelect, CLabel } from "@coreui/react";
import AutoPayConfirmModal from "./AutoPayConfirmModal";
import ChangePaymentConfirmModal from "./ChangePaymentConfirmModal";
import { AutopayAccountManagementList } from "./AutopayAccountManagementList";
import { AutoPayDebitDetails } from "./AutoPayDebitDetails";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { AccountSummaryData } from "../../../AccountSummary.slice";
import {
  canEnableAutopayForAccount,
  disableAutopayForAccounts,
  enableAutopayForAccounts,
  enableAutopayWithAssignments,
  formatAutopayApiError,
  isDelegateAutopayAccount,
  isModalActionCancelled,
  isOwnerEnrolledInAutopay,
  setAutopayPaymentMethodForAccount,
} from "./setAutopayPaymentMethod";
import { AutopayInfoAlert } from "./AutopayInfoAlert";
import {
  buildAutopayChangeSourcePaymentMethodOptions,
  buildAutopayEnrollmentPaymentMethodOptions,
  AutopayPaymentMethodOption,
} from "./autopayPaymentMethodOptions";
import {
  getDefaultEligibleAutopayPaymentMethodId,
  isEligibleAutopayPaymentMethod,
} from "../../PaymentMethods/utils";

type AutoPayComponentMode = "dashboard" | "full";

interface PropsInterface {
  mode?: AutoPayComponentMode;
}

export const AutoPayComponent: React.FC<PropsInterface> = ({
  mode = "full",
}) => {
  const [autoPayConfirm, setAutoPayConfirm] = useState<any>(false);
  const [autopayChangeConfirm, setAutopayChangeConfirm] = useState<any>(false);
  const [pendingPaymentMethodIdsByAccount, setPendingPaymentMethodIdsByAccount] =
    useState<Record<string, string>>({});
  const [isAutopayChangeSubmitting, setIsAutopayChangeSubmitting] =
    useState(false);
  const [isAutopayBatchSubmitting, setIsAutopayBatchSubmitting] =
    useState(false);
  const accountSummary = useAppSelector((state) => state.accountSummary);
  const methods = useAppSelector((state) => state.payment.methods);

  const { data: paymentMethods, defaultPaymentMethod } = methods;
  const { data, activeTenantAccountIndex } = accountSummary;
  const accounts = data ?? [];

  const dispatch = useAppDispatch();

  const accountSummaryData = accounts[activeTenantAccountIndex];
  const tenantAccountId = accountSummaryData?.id;
  const isDelegateAutopay = accountSummaryData
    ? isDelegateAutopayAccount(accountSummaryData)
    : false;
  const isOwnerAutopayEnrolled = accountSummaryData
    ? isOwnerEnrolledInAutopay(accountSummaryData)
    : false;
  const isAutopayEnabled = accountSummaryData?.autopay?.enabled;
  const autopayPaymentMethodId = accountSummaryData?.autopay?.paymentMethodId;
  const hasDefaultPaymentMethod = Boolean(defaultPaymentMethod?.id);
  const hasEligibleDefaultPaymentMethod =
    hasDefaultPaymentMethod &&
    isEligibleAutopayPaymentMethod(defaultPaymentMethod);
  const eligiblePaymentMethods = paymentMethods.filter(
    isEligibleAutopayPaymentMethod
  );
  const useMultiAccountFullView = mode === "full" && accounts.length > 1;
  const enrolledCount = accounts.filter((account) =>
    isOwnerEnrolledInAutopay(account)
  ).length;
  const defaultPendingPaymentMethodId = getDefaultEligibleAutopayPaymentMethodId(
    paymentMethods,
    hasDefaultPaymentMethod ? defaultPaymentMethod : false
  );

  useEffect(() => {
    setPendingPaymentMethodIdsByAccount((current) => {
      const next: Record<string, string> = {};

      accounts.forEach((account) => {
        if (!canEnableAutopayForAccount(account)) {
          return;
        }

        next[account.id] =
          current[account.id] || defaultPendingPaymentMethodId || "";
      });

      return next;
    });
  }, [accounts, defaultPendingPaymentMethodId]);

  const enrollmentPaymentMethodOptions =
    buildAutopayEnrollmentPaymentMethodOptions(paymentMethods);
  const changeSourcePaymentMethodOptions =
    buildAutopayChangeSourcePaymentMethodOptions(
      paymentMethods,
      autopayPaymentMethodId
    );

  const getPendingPaymentMethodIdForAccount = (accountId: string) =>
    pendingPaymentMethodIdsByAccount[accountId] || defaultPendingPaymentMethodId;

  const confirmAutopayAction = async ({
    action,
    targetAccounts,
    paymentMethodId,
    accountPaymentMethodsByAccount,
    usePerAccountPaymentSources = false,
    showUnaffectedAccountsHint = false,
  }: {
    action: "enable" | "disable";
    targetAccounts: AccountSummaryData[];
    paymentMethodId?: string;
    accountPaymentMethodsByAccount?: Record<string, AutopayPaymentMethodOption>;
    usePerAccountPaymentSources?: boolean;
    showUnaffectedAccountsHint?: boolean;
  }) => {
    await new Promise((resolve, reject) => {
      setAutoPayConfirm(
        <AutoPayConfirmModal
          action={action}
          targetAccounts={targetAccounts}
          paymentMethodId={paymentMethodId}
          accountPaymentMethodsByAccount={accountPaymentMethodsByAccount}
          usePerAccountPaymentSources={usePerAccountPaymentSources}
          showUnaffectedAccountsHint={showUnaffectedAccountsHint}
          resolve={resolve}
          reject={reject}
        />
      );
    });
  };

  const runEnableAutopay = async (targetAccounts: AccountSummaryData[]) => {
    if (targetAccounts.length === 0) {
      return;
    }

    const enrollments = targetAccounts.map((account) => ({
      tenantAccountId: account.id,
      paymentMethodId: getPendingPaymentMethodIdForAccount(account.id),
    }));

    if (enrollments.some((enrollment) => !enrollment.paymentMethodId)) {
      dispatch(
        pushToast({
          message: "Select a payment method before enabling AutoPay",
          type: "error",
        })
      );
      return;
    }

    if (
      enrollments.some(
        (enrollment) =>
          !isEligibleAutopayPaymentMethod(
            paymentMethods.find(
              (paymentMethod) => paymentMethod.id === enrollment.paymentMethodId
            ) ?? { type: "" }
          )
      )
    ) {
      dispatch(
        pushToast({
          message: "Select an active, non-expired payment method for AutoPay",
          type: "error",
        })
      );
      return;
    }

    const uniquePaymentMethodIds = new Set(
      enrollments.map((enrollment) => enrollment.paymentMethodId)
    );
    const paymentMethodId =
      uniquePaymentMethodIds.size === 1
        ? enrollments[0].paymentMethodId
        : undefined;

    const accountPaymentMethodsByAccount = Object.fromEntries(
      enrollments.flatMap((enrollment) => {
        const paymentMethod = paymentMethods.find(
          (paymentMethodOption) =>
            paymentMethodOption.id === enrollment.paymentMethodId
        );

        return paymentMethod
          ? [[enrollment.tenantAccountId, paymentMethod]]
          : [];
      })
    ) as Record<string, AutopayPaymentMethodOption>;

    try {
      await confirmAutopayAction({
        action: "enable",
        targetAccounts,
        paymentMethodId,
        accountPaymentMethodsByAccount,
        usePerAccountPaymentSources: uniquePaymentMethodIds.size > 1,
      });

      if (uniquePaymentMethodIds.size === 1) {
        await enableAutopayForAccounts(
          dispatch,
          enrollments.map((enrollment) => enrollment.tenantAccountId),
          enrollments[0].paymentMethodId
        );
      } else {
        await enableAutopayWithAssignments(dispatch, enrollments);
      }
      setAutoPayConfirm(false);
    } catch (e: any) {
      setAutoPayConfirm(false);
      if (isModalActionCancelled(e)) {
        return;
      }
      throw e;
    }
  };

  const runDisableAutopay = async (
    targetAccounts: AccountSummaryData[],
    showUnaffectedAccountsHint = false
  ) => {
    if (targetAccounts.length === 0) {
      return;
    }

    try {
      await confirmAutopayAction({
        action: "disable",
        targetAccounts,
        showUnaffectedAccountsHint,
      });

      await disableAutopayForAccounts(
        dispatch,
        targetAccounts.map((account) => account.id)
      );
      setAutoPayConfirm(false);
    } catch (e: any) {
      setAutoPayConfirm(false);
      if (isModalActionCancelled(e)) {
        return;
      }
      throw e;
    }
  };

  const handleManagementEnableAccounts = async (
    targetAccounts: AccountSummaryData[]
  ) => {
    setIsAutopayBatchSubmitting(true);
    try {
      await runEnableAutopay(targetAccounts);
    } catch (e: any) {
      console.error(e);
      dispatch(
        pushToast({
          message: formatAutopayApiError(e),
          type: "error",
        })
      );
    } finally {
      setIsAutopayBatchSubmitting(false);
    }
  };

  const handleManagementDisableAccounts = async (
    targetAccounts: AccountSummaryData[]
  ) => {
    setIsAutopayBatchSubmitting(true);
    try {
      await runDisableAutopay(targetAccounts, false);
    } catch (e: any) {
      console.error(e);
      dispatch(
        pushToast({
          message: formatAutopayApiError(e),
          type: "error",
        })
      );
    } finally {
      setIsAutopayBatchSubmitting(false);
    }
  };

  const handleAutopayChangeConfirm = async () => {
    const targetAccountId =
      autopayChangeConfirm?.tenantAccountId ?? tenantAccountId;

    if (!targetAccountId || !autopayChangeConfirm?.id) {
      return;
    }

    setIsAutopayChangeSubmitting(true);
    try {
      await setAutopayPaymentMethodForAccount(
        dispatch,
        targetAccountId,
        autopayChangeConfirm.id
      );
      setAutopayChangeConfirm(false);
    } catch (e: any) {
      console.error(e);
      dispatch(
        pushToast({
          message: formatAutopayApiError(e),
          type: "error",
        })
      );
    } finally {
      setIsAutopayChangeSubmitting(false);
    }
  };

  const renderDashboardMultiAccountLink = () => {
    if (mode !== "dashboard" || accounts.length <= 1) {
      return null;
    }

    return (
      <p className="text-muted">
        {enrolledCount} of {accounts.length} service accounts are enrolled in
        AutoPay.{" "}
        <Link to="/auto-pay">Manage AutoPay for all service accounts</Link>.
      </p>
    );
  };

  const renderSingleAccountDelegate = () => (
    <>
      {renderDashboardMultiAccountLink()}
      <AutopayInfoAlert enrolled className="mb-3" />
      <ul className="list-group">
        <li className="list-group-item d-flex list-group-item-action justify-content-between align-items-center">
          <span className="font-weight-bold">AutoPay</span>
          <CSwitch
            key="auto_pay"
            className="preference-switch"
            color="success"
            labelOn="On"
            labelOff="Off"
            value="success"
            shape="pill"
            checked={Boolean(isAutopayEnabled)}
            disabled
          />
        </li>
      </ul>
    </>
  );

  const renderSingleAccountEnrolled = () => (
    <>
      {renderDashboardMultiAccountLink()}
      <p>
        <strong>You are currently enrolled in AutoPay.</strong>
        <br />
        <span className="text-muted">
          Tap the toggle button below to turn AutoPay off.
        </span>
      </p>
      <p className="autopay-debit-details-text">
        AutoPay will debit <AutoPayDebitDetails />.
      </p>
      <CFormGroup>
        <CLabel htmlFor="autopay-payment-source">
          Payment source for this service account
        </CLabel>
        <CSelect
          id="autopay-payment-source"
          value={autopayPaymentMethodId || ""}
          disabled={isAutopayChangeSubmitting}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            const newId = e.target.value;
            if (!newId || newId === autopayPaymentMethodId) {
              return;
            }

            const selectedPm = paymentMethods.find(
              (pm: any) => pm.id === newId
            );
            if (selectedPm && isEligibleAutopayPaymentMethod(selectedPm)) {
              setAutopayChangeConfirm({
                ...selectedPm,
                tenantAccountId,
              });
            }
          }}
        >
          {changeSourcePaymentMethodOptions}
        </CSelect>
        <p className="text-muted mb-0 mt-2">
          You can also change your AutoPay payment source on the{" "}
          <Link to="/payment-methods">Payment Methods</Link> page.
        </p>
      </CFormGroup>
      <ul className="list-group">
        <li className="list-group-item d-flex list-group-item-action justify-content-between align-items-center">
          <span className="font-weight-bold">AutoPay</span>
          <CSwitch
            key="auto_pay"
            className="preference-switch"
            color="success"
            labelOn="On"
            labelOff="Off"
            value="success"
            shape="pill"
            checked={isAutopayEnabled}
            onChange={async () => {
              if (!accountSummaryData) {
                return;
              }

              try {
                await runDisableAutopay(
                  [accountSummaryData],
                  mode === "dashboard" && accounts.length > 1
                );
              } catch (e: any) {
                if (!isModalActionCancelled(e)) {
                  console.error(e);
                  dispatch(
                    pushToast({
                      message: formatAutopayApiError(e),
                      type: "error",
                    })
                  );
                }
              }
            }}
          />
        </li>
      </ul>
    </>
  );

  const renderSingleAccountNotEnrolled = () => (
    <>
      {renderDashboardMultiAccountLink()}
      <p>
        <strong>You are not currently enrolled in AutoPay.</strong>
      </p>
      {!eligiblePaymentMethods.length ? (
        <p>
          To enable AutoPay, please add an active payment method.{" "}
          <Link to="/payment-methods">Go to Payment Methods page</Link>
        </p>
      ) : (
        <>
          {!hasEligibleDefaultPaymentMethod && (
            <>
              <p>
                Please choose the payment method you would like to enable for
                AutoPay.
              </p>
              <Formik
                initialValues={{
                  paymentMethodId: "",
                }}
                validationSchema={Yup.object().shape({
                  paymentMethodId: Yup.string()
                    .required("Please make a selection")
                    .test(
                      "eligible-autopay-payment-method",
                      "Please select an active, non-expired payment method",
                      function (value) {
                        if (!value) {
                          return false;
                        }

                        const selectedPaymentMethod = paymentMethods.find(
                          (paymentMethod) => paymentMethod.id === value
                        );

                        return (
                          Boolean(selectedPaymentMethod) &&
                          isEligibleAutopayPaymentMethod(selectedPaymentMethod!)
                        );
                      }
                    ),
                })}
                onSubmit={async (values, { setSubmitting }) => {
                  if (!accountSummaryData) {
                    setSubmitting(false);
                    return;
                  }

                  try {
                    setPendingPaymentMethodIdsByAccount((current) => ({
                      ...current,
                      [accountSummaryData.id]: values.paymentMethodId,
                    }));
                    await runEnableAutopay([accountSummaryData]);
                  } catch (e: any) {
                    if (isModalActionCancelled(e)) {
                      setSubmitting(false);
                      return;
                    }
                    console.error(e);
                    dispatch(
                      pushToast({
                        message: formatAutopayApiError(e),
                        type: "error",
                      })
                    );
                    setSubmitting(false);
                    return;
                  }

                  setSubmitting(false);
                }}
              >
                {({ errors, touched }) => (
                  <Form>
                    <CFormGroup
                      className={
                        errors.paymentMethodId && touched.paymentMethodId
                          ? "error"
                          : ""
                      }
                    >
                      <Field as={CSelect} name="paymentMethodId">
                        <option value="">Select payment method</option>
                        {enrollmentPaymentMethodOptions}
                      </Field>
                      <div className="error-text">
                        {errors.paymentMethodId &&
                          touched.paymentMethodId &&
                          errors.paymentMethodId}
                      </div>
                    </CFormGroup>
                    <p>
                      You may add additional payment sources on the{" "}
                      <Link to="/payment-methods">Payment Methods</Link> page.
                    </p>
                    <ul className="list-group">
                      <li className="list-group-item d-flex list-group-item-action justify-content-between align-items-center">
                        <span className="font-weight-bold">AutoPay</span>
                        <CSwitch
                          type="submit"
                          key="auto_pay"
                          className="preference-switch"
                          color="success"
                          labelOn="On"
                          labelOff="Off"
                          value="success"
                          shape="pill"
                          checked={isAutopayEnabled}
                        />
                      </li>
                    </ul>
                  </Form>
                )}
              </Formik>
            </>
          )}
          {hasEligibleDefaultPaymentMethod && (
            <>
              <p className="autopay-debit-details-text">
                Enabling AutoPay will debit <AutoPayDebitDetails />.
                <br />
                <span className="text-muted">
                  To debit from a different payment source for this service
                  account, choose a payment method on the{" "}
                  <Link to="/payment-methods">Payment Methods</Link> page
                  before enabling AutoPay.
                </span>
              </p>
              <p>Tap the toggle button below to turn AutoPay on.</p>
              <ul className="list-group">
                <li className="list-group-item d-flex list-group-item-action justify-content-between align-items-center">
                  <span className="font-weight-bold">AutoPay</span>
                  <CSwitch
                    key="auto_pay"
                    className="preference-switch"
                    color="success"
                    labelOn="On"
                    labelOff="Off"
                    value="success"
                    shape="pill"
                    checked={isAutopayEnabled}
                    onChange={async () => {
                      if (!accountSummaryData) {
                        return;
                      }

                      try {
                        setPendingPaymentMethodIdsByAccount((current) => ({
                          ...current,
                          [accountSummaryData.id]:
                            defaultPendingPaymentMethodId,
                        }));
                        await runEnableAutopay([accountSummaryData]);
                      } catch (e: any) {
                        if (isModalActionCancelled(e)) {
                          return;
                        }
                        console.error(e);
                        dispatch(
                          pushToast({
                            message: formatAutopayApiError(e),
                            type: "error",
                          })
                        );
                      }
                    }}
                  />
                </li>
              </ul>
            </>
          )}
        </>
      )}
    </>
  );

  const renderMultiAccountFullView = () => {
    if (!eligiblePaymentMethods.length) {
      return (
        <p>
          To enable AutoPay, please add an active payment method.{" "}
          <Link to="/payment-methods">Go to Payment Methods page</Link>
        </p>
      );
    }

    return (
      <>
        <p className="text-muted">
          Manage AutoPay enrollment and payment sources for each service account
          below. Payment methods are shared across your accounts.
        </p>
        <AutopayAccountManagementList
          accounts={accounts}
          currentAccountId={tenantAccountId ?? ""}
          paymentMethods={paymentMethods}
          pendingPaymentMethodIdsByAccount={pendingPaymentMethodIdsByAccount}
          onPendingPaymentMethodChange={(accountId, paymentMethodId) => {
            setPendingPaymentMethodIdsByAccount((current) => ({
              ...current,
              [accountId]: paymentMethodId,
            }));
          }}
          disabled={isAutopayBatchSubmitting || isAutopayChangeSubmitting}
          onEnableAccounts={handleManagementEnableAccounts}
          onDisableAccounts={handleManagementDisableAccounts}
          onPaymentMethodChangeRequest={(account, paymentMethod) => {
            setAutopayChangeConfirm({
              ...paymentMethod,
              tenantAccountId: account.id,
            });
          }}
        />
      </>
    );
  };

  return (
    <>
      <div className="card">
        <div className="card-body">
          <h2 className="card-title">AutoPay</h2>
          <div>
            {useMultiAccountFullView
              ? renderMultiAccountFullView()
              : isDelegateAutopay
                ? renderSingleAccountDelegate()
                : isOwnerAutopayEnrolled
                  ? renderSingleAccountEnrolled()
                  : renderSingleAccountNotEnrolled()}
            {autoPayConfirm}
            {autopayChangeConfirm && (
              <ChangePaymentConfirmModal
                paymentMethod={autopayChangeConfirm}
                tenantAccountId={autopayChangeConfirm.tenantAccountId}
                resolve={async () => {
                  await handleAutopayChangeConfirm();
                }}
                reject={() => {
                  if (!isAutopayChangeSubmitting) {
                    setAutopayChangeConfirm(false);
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
